import type { ChipVariant } from '@/components/ui/Chip/Chip'
import type { ExportColumn } from '@/components/ui'
import type { PriceReview, PriceReviewLine, ReviewStatus } from '../api/reviewTypes'

export type RowTone = 'flagged' | 'inactive' | null

const round = (v: number, places: number) => {
  const f = 10 ** places
  return Math.round((v + Number.EPSILON) * f) / f
}

/** Tariff $ = New Price × Tariff %. Null until there is a new price. */
export function tariffAmount(newPrice: number | null, pct: number | null): number | null {
  if (newPrice === null) return null
  return round((newPrice * (pct ?? 0)) / 100, 6)
}

/** Total = New Price + Tariff $. */
export function totalPrice(newPrice: number | null, pct: number | null): number | null {
  const t = tariffAmount(newPrice, pct)
  return newPrice === null || t === null ? null : round(newPrice + t, 6)
}

function sameUnit(a: string | null | undefined, b: string | null | undefined): boolean {
  return !!a && !!b && a.trim().toLowerCase() === b.trim().toLowerCase()
}

type CostFields = Pick<PriceReviewLine, 'cost_price' | 'cost_uom_name' | 'price_uom_name'>

/**
 * New margin in percent points: (New − Cost) / New. Null with no new price or
 * cost, and when cost and price are per different units: a margin across units
 * would be a wrong number, so it is withheld instead.
 */
export function newMarginPct(line: Pick<PriceReviewLine, 'new_price'> & CostFields): number | null {
  const { new_price: np, cost_price: cost } = line
  if (np === null || np <= 0 || cost === null) return null
  if (!sameUnit(line.cost_uom_name, line.price_uom_name)) return null
  return ((np - cost) / np) * 100
}

/** Margin on the price the customer pays TODAY, at today's cost. Same unit rule. */
export function currentMarginPct(line: Pick<PriceReviewLine, 'old_price'> & CostFields): number | null {
  const { old_price: p, cost_price: cost } = line
  if (p === null || p <= 0 || cost === null) return null
  if (!sameUnit(line.cost_uom_name, line.price_uom_name)) return null
  return ((p - cost) / p) * 100
}

type UpcomingFields = Pick<PriceReviewLine, 'upcoming_cost_price' | 'upcoming_cost_uom_name' | 'price_uom_name'>

/** Margin on `price` once the scheduled vendor cost starts. Null when there is none or its unit differs from the price's. */
export function marginAtScheduledCost(price: number | null, line: UpcomingFields): number | null {
  const up = line.upcoming_cost_price
  if (price === null || price <= 0 || up === null || up <= 0) return null
  if (!sameUnit(line.upcoming_cost_uom_name, line.price_uom_name)) return null
  return ((price - up) / price) * 100
}

/** How much the scheduled vendor cost differs from today's, in percent. Null unless both are in the same unit. */
export function scheduledCostChangePct(line: UpcomingFields & Pick<PriceReviewLine, 'cost_price' | 'cost_uom_name'>): number | null {
  const up = line.upcoming_cost_price
  const cost = line.cost_price
  if (up === null || cost === null || cost <= 0) return null
  if (!sameUnit(line.upcoming_cost_uom_name, line.cost_uom_name)) return null
  return ((up - cost) / cost) * 100
}

/**
 * The price that gives `marginPct` on `cost`, rounded UP to the next cent so the
 * result is never below the margin that was asked for. Null for a margin that
 * has no price (100% or more, -100% or less) or a cost that is missing.
 */
export function priceForMargin(cost: number | null, marginPct: number): number | null {
  if (cost === null || !Number.isFinite(cost) || cost <= 0) return null
  if (!Number.isFinite(marginPct) || marginPct >= 100 || marginPct <= -100) return null
  const raw = cost / (1 - marginPct / 100)
  return Math.ceil(raw * 100 - 1e-9) / 100
}

/** True when cost is known but sits in a different unit than the price. */
export function unitsDiffer(line: CostFields): boolean {
  return line.cost_price !== null && !sameUnit(line.cost_uom_name, line.price_uom_name)
}

/** Yellow beats pink: a flag is a person's decision, "no volume" is a computed fact. */
export function rowTone(line: Pick<PriceReviewLine, 'flagged' | 'inactive'>): RowTone {
  if (line.flagged) return 'flagged'
  if (line.inactive) return 'inactive'
  return null
}

/** Row tint plus a 4 px left accent, so the state never rests on colour alone. */
export const ROW_TONE_CLASS: Record<Exclude<RowTone, null>, string> = {
  flagged: 'border-l-warning bg-warning-bg',
  inactive: 'border-l-danger bg-danger-bg',
}

export function uomLabel(uom: string | null): string {
  if (!uom) return ''
  return uom.charAt(0).toUpperCase() + uom.slice(1)
}

function qty(n: number): string {
  return n.toLocaleString('en-US', { maximumFractionDigits: 2 })
}

const MONTHS = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December']

/**
 * "July, 5,400 Kg" for this year, "October '25, 5 Kg" for an earlier one: the
 * label the sheet used. "—" when the customer never ordered it.
 */
export function lastOrderLabel(
  line: Pick<PriceReviewLine, 'last_order_date' | 'last_order_qty' | 'last_order_uom'>,
  today: Date = new Date(),
): string {
  if (!line.last_order_date) return '—'
  const [y, m] = line.last_order_date.split('-').map(Number)
  if (!y || !m) return '—'
  const month = MONTHS[m - 1] ?? ''
  const when = y === today.getFullYear() ? month : `${month} '${String(y).slice(-2)}`
  if (line.last_order_qty === null) return when
  return `${when}, ${qty(line.last_order_qty)} ${uomLabel(line.last_order_uom)}`.trim()
}

/** "8,000 Kg", or "-" for no volume (the sheet's convention) and "—" when unknown. */
export function rollingLabel(line: Pick<PriceReviewLine, 'rolling_12m_qty' | 'last_order_uom'>): string {
  if (line.rolling_12m_qty === null) return '—'
  if (line.rolling_12m_qty <= 0) return '-'
  return `${qty(line.rolling_12m_qty)} ${uomLabel(line.last_order_uom)}`.trim()
}

export interface ReviewSummary {
  changes: number
  discontinues: number
  kept: number
  /** Marked Change but with no price yet. */
  missingPrice: number
  /** Lines that will be pushed and are still flagged for discussion. */
  flaggedActing: number
}

export function summarize(lines: readonly PriceReviewLine[]): ReviewSummary {
  const s: ReviewSummary = { changes: 0, discontinues: 0, kept: 0, missingPrice: 0, flaggedActing: 0 }
  for (const ln of lines) {
    if (ln.action === 'keep') {
      s.kept += 1
      continue
    }
    if (ln.action === 'discontinue') s.discontinues += 1
    else {
      s.changes += 1
      if (ln.new_price === null) s.missingPrice += 1
    }
    if (ln.flagged) s.flaggedActing += 1
  }
  return s
}

/** Sales edits a draft; only an admin edits a submitted review; everything else is locked. */
export function canEditLines(status: ReviewStatus, isAdmin: boolean): boolean {
  return status === 'draft' || (status === 'submitted' && isAdmin)
}

export const STATUS_META: Record<ReviewStatus, { label: string; variant: ChipVariant }> = {
  draft: { label: 'Draft', variant: 'neutral' },
  submitted: { label: 'Needs approval', variant: 'warning' },
  approved: { label: 'Approved', variant: 'info' },
  pushed: { label: 'Pushed to Odoo', variant: 'success' },
  push_failed: { label: 'Push failed', variant: 'danger' },
  rejected: { label: 'Rejected', variant: 'danger' },
  cancelled: { label: 'Cancelled', variant: 'neutral' },
}

/** A review that will still change prices or already has. Drives the "changes" count on lists. */
export function reviewHasStarted(review: Pick<PriceReview, 'status'>): boolean {
  return review.status === 'approved' || review.status === 'pushed' || review.status === 'push_failed'
}

/**
 * Columns in the original workbook's order, plus Notes. Tariff is exported as a
 * fraction (0.2 = 20%) like the sheet's % cell, which the price letter upload
 * reads back correctly; Margin % stays in percent points as on the sheet.
 */
export function reviewExportColumns(today: Date = new Date()): ExportColumn<PriceReviewLine>[] {
  return [
    { key: 'product', header: 'Product', value: (l) => l.product_name },
    { key: 'part', header: 'Customer Part #', value: (l) => l.customer_part_number },
    { key: 'old', header: 'Old Price', value: (l) => l.old_price },
    { key: 'new', header: 'New Price', value: (l) => l.new_price },
    { key: 'cost', header: 'Our Cost', value: (l) => l.cost_price },
    { key: 'tariff_pct', header: 'Tariff', value: (l) => (l.tariff_pct === null ? 0 : l.tariff_pct / 100) },
    { key: 'tariff_amt', header: 'Tariff $', value: (l) => l.tariff_amount ?? 0 },
    { key: 'total', header: 'Total Price', value: (l) => l.total_price },
    {
      key: 'new_margin',
      header: 'New Margin',
      value: (l) => {
        const m = newMarginPct(l)
        return m === null ? null : round(m / 100, 4)
      },
    },
    { key: 'margin', header: 'Margin %', value: (l) => (l.hist_margin_pct === null ? null : round(l.hist_margin_pct, 1)) },
    { key: 'last_order', header: 'Last Order and Volume Dlvd', value: (l) => lastOrderLabel(l, today) },
    { key: 'rolling', header: 'Rolling 12 months', value: (l) => rollingLabel(l) },
    {
      key: 'notes',
      header: 'Notes',
      value: (l) => [l.action === 'discontinue' ? 'Discontinue' : null, l.note].filter(Boolean).join(' — ') || null,
    },
  ]
}
